import React, { useEffect, useLayoutEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useOktaAuth } from "@okta/okta-react";
import { RootState } from "../../store/store";
import {
  resetForecastState,
  resetResultVisualizationState,
  setForecastFilter,
  setRcaFilter,
  setResultVisualizationFilter,
} from "../../store/filterSlice";
import { useForecastClusterFilter } from "../../api/filtersApi";
import { postAPI } from "../../API";
import {
  setChartData,
  setAverageData,
  resetResultVisulizationChartData,
  resultVisulizationBackTestingTableDataSetter,
  updateBackTestingTotalPages,
  setLoader,
  resetresultVisulizationBackTestingTableState,
  resetPredefinePaginationState,
} from "../../store/resultVisualizationPredefined";
import {
  calculateTotalPages,
  resultVisulizationPredefineDefaultGroupBy,
  mapperForGroupByLableToKey,
  resultVisualisationAverageAPI,
  resultVisualisationTableAPI,
} from "../../api/forecastPageApi";
import { PAGE_LIMIT } from "../../utils/constant";
import { useNavigate, useSearchParams } from "react-router-dom";
import FilterResetModal from "../../baseComponent/Filter/FilterResetModal";
import FilterButtons from "../../baseComponent/Filter/FilterButtons";
import FilterDropdown from "../../baseComponent/Filter/FilterDropdown";
import { resetForecastData } from "../../store/foreCastDataSlice";
import { resetForecastDecomState } from "../../store/foreCastDecomSlice";
import {
  resetForecastBackTestingApiDataLoading,
  resetForecastBackTestingAverageState,
  resetForecastBackTestingChartState,
  resetForecastBackTestingTableState,
} from "../../store/foreCastBackTestingDataSlice";
import { DynamicMappings } from "../../utils/dynamicMappings";
import ComparisonResultPredefinedRuns from "../../pages/ResultVisualization/ComparisonResultPredefinedRuns";
import ResultVisulizationBacktestingResults from "../../pages/ResultVisualization/ResultVisulizationBacktestingResults";

const ResultVisualizationPredefinedFilter: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [tablePayload, setTablePayload] = useState(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const navigate = useNavigate();
  const { authState } = useOktaAuth();
  const dispatch = useDispatch();
  const dataShowKeyValueMappings = DynamicMappings();
  const overallAverage = useSelector(
    (state: RootState) => state.resultVisualizationData.average
  );
  const userData = useSelector((state: RootState) => state.user);
  const filter = useSelector(
    (state: RootState) => state.resultVisualizationFilter
  );
  const resultVisulizationBackTestingPagination = useSelector(
    (state: RootState) => state.resultVisulizationBackTestingPagination
  );

  const lagDropdownData = useSelector(
    (state: RootState) => state.lagDropdownData
  );

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const payloadForChart: any = {
    brandName: filter?.brand,
    subBrandName: filter?.subBrand,
    rank: filter?.rankPredefined?.map((item) => +item),
    shortTermMidTermCode: filter?.term.toString(),
    productLineName: filter?.productLine,
    productGroupName: filter?.productGroup,
    materialGlobalTradeItemNumberDescription: filter?.planningItem,
    keyAccount: filter?.keyAccount,
    packageSizeName: filter?.size,
    productCategoryName: filter?.category,
    portfolioGroupName: filter?.mg4PortfolioGroup,
    packageName: filter?.count,
    packageTypeName: filter?.container,
    packTypeName: filter.ppg,
    salesRegionName: filter?.salesRegion,
    salesCountryCode: filter?.salesCountry,
    forecastModuleCode: filter?.ac1ac2,
    forecastEngineSegmentationId: filter?.segment,
    customerGlobalName: filter?.global,
    customerSectorNumber: filter?.customerSector,
    customerRegionName: filter?.customerRegion,
    customerGroupName: filter?.customerGroup,
    customerBusinessUnitName: filter?.businessUnit,
    customerMarketUnitName: filter?.marketUnit,
    ibpDemandGroup: filter?.ibpDemandGroup,
    customerTradeSegmentDescription: filter?.cg1TrageSegment,
    salesSectorName: filter?.salesSector,
    salesOrganizationCode: filter?.businessOrg,
    salesDivisionName: filter?.division,
    forecastEngineClusterId: filter?.clusters,
    snapshotDate:
      filter?.term.toString() === "ST"
        ? filter?.shortSnapshotDate
        : filter?.midSnapshotDate,
    sector: userData?.currentSector?.sector,
    market: userData?.currentSector?.market,
    // LATAM Key
    flavorName: filter?.flavourNames,
    customerChannelName: filter?.channels,
    customerDemandGroup: filter?.demandGroups,
    customerSubChannelName: filter?.subChannels,
    customerRegionalCluster: filter?.regionalClusters,
    locationGlobalName: filter?.globalCode,
    locationSectorName: filter?.locationSector,
    locationCountryName: filter?.locationCountry,
    planningLocationId: filter?.planningLocationId,
    locationRegionName: filter?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
    salesZoneName: filter?.zone,
    eachUPC: filter?.eachUPC,
  };

  if (
    userData?.sections?.predefinedRuns?.table?.lagDropdown &&
    filter?.term?.toString() === "ST" &&
    lagDropdownData.predefinedLagForSt !== null
  ) {
    payloadForChart["forecastLagNumber"] = Number(
      lagDropdownData.predefinedLagForSt
    );
  }
  if (
    userData?.sections?.predefinedRuns?.table?.lagDropdown &&
    filter?.term?.toString() === "MT" &&
    lagDropdownData.predefinedLagForMt !== null
  ) {
    payloadForChart["forecastLagNumber"] = Number(
      lagDropdownData.predefinedLagForMt
    );
  }

  const backtestingPayloadFilters = {
    shortTermMidTermCode: filter?.term.toString(),
    // uncommenting the snapshot date since we need to pass it in API 16-05-2025
    snapshotDate:
      filter?.term.toString() === "ST"
        ? filter?.shortSnapshotDate
        : filter?.midSnapshotDate,
    brandName: filter?.brand,
    subBrandName: filter?.subBrand,
    rank: filter?.rankBackTestingPredefined?.map((item) => +item),
    productLineName: filter?.productLine,
    productGroupName: filter?.productGroup,
    materialGlobalTradeItemNumberDescription: filter?.planningItem,
    keyAccount: filter?.keyAccount,
    packageSizeName: filter?.size,
    productCategoryName: filter?.category,
    portfolioGroupName: filter?.mg4PortfolioGroup,
    packageName: filter?.count,
    packageTypeName: filter?.container,
    packTypeName: filter.ppg,
    salesRegionName: filter?.salesRegion,
    salesCountryCode: filter?.salesCountry,
    forecastModuleCode: filter?.ac1ac2,
    forecastEngineSegmentationId: filter?.segment,
    customerGlobalName: filter?.global,
    customerSectorNumber: filter?.customerSector,
    customerRegionName: filter?.customerRegion,
    customerGroupName: filter?.customerGroup,
    customerBusinessUnitName: filter?.businessUnit,
    customerMarketUnitName: filter?.marketUnit,
    ibpDemandGroup: filter?.ibpDemandGroup,
    customerTradeSegmentDescription: filter?.cg1TrageSegment,
    salesSectorName: filter?.salesSector,
    salesOrganizationCode: filter?.businessOrg,
    salesDivisionName: filter?.division,
    forecastEngineClusterId: filter?.clusters,
    pageNumber: 1,
    pageSize: PAGE_LIMIT,
    sector: userData?.currentSector?.sector,
    market: userData?.currentSector?.market,
    // LATAM Key
    flavorName: filter?.flavourNames,
    customerChannelName: filter?.channels,
    customerDemandGroup: filter?.demandGroups,
    customerSubChannelName: filter?.subChannels,
    customerRegionalCluster: filter?.regionalClusters,
    locationGlobalName: filter?.globalCode,
    locationSectorName: filter?.locationSector,
    locationCountryName: filter?.locationCountry,
    planningLocationId: filter?.planningLocationId,
    locationRegionName: filter?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
    salesZoneName: filter?.zone,
    eachUPC: filter?.eachUPC,
  };

  const { data: clusterApiData } = useForecastClusterFilter(
    authState?.accessToken?.accessToken!,
    "cluster",
    userData?.currentSector ? userData?.currentSector : {}
  );

  const measurementToggleActivePredefinedTable = useSelector(
    (state: RootState) =>
      state?.resultVisualizationData?.measurementToggleActivePredefinedTable
  );

  const measurementToggleActiveComparisonPredefined = useSelector(
    (state: RootState) =>
      state.resultVisualizationData.measurementToggleActiveComparisonPredefined
  );

  const resetFilter = () => {
    dispatch(
      resetResultVisualizationState({
        key: "clusters",
        value: clusterApiData?.clusters,
      })
    );
    dispatch(setLoader(false));
    dispatch(resetResultVisulizationChartData());
    dispatch(resetresultVisulizationBackTestingTableState());
    dispatch(resetPredefinePaginationState());

    dispatch(
      resetForecastState({ key: "clusters", value: clusterApiData?.clusters })
    );
    dispatch(resetForecastData());
    dispatch(resetForecastDecomState());
    dispatch(resetForecastBackTestingTableState());
    dispatch(resetForecastBackTestingChartState());
    dispatch(resetForecastBackTestingAverageState());
    dispatch(resetForecastBackTestingApiDataLoading());
    handleClose();
    if (userData?.currentSector?.sector === "PFNA") {
      dispatch(
        setRcaFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );
      dispatch(
        setForecastFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );

      dispatch(
        setResultVisualizationFilter({
          key: "ac1ac2",
          value: ["AC1"],
        })
      );
    }
  };

  useEffect(() => {
    if (
      resultVisulizationBackTestingPagination?.totalPages > 1 &&
      resultVisulizationBackTestingPagination?.isDataShownClicked === false
    ) {
      makeBackTesingApiCall(backtestingPayloadFilters, authState);
    }
  }, [resultVisulizationBackTestingPagination?.pageNumber]);

  const forecastAnalysisApiRequestBody: any = {
    shortTermMidTermCode: filter?.term.toString(),
    rank: filter?.rankPredefined?.map((item) => +item),
    brandName: filter?.brand,
    subBrandName: filter?.subBrand,
    productLineName: filter?.productLine,
    productGroupName: filter?.productGroup,
    materialGlobalTradeItemNumberDescription: filter?.planningItem,
    keyAccount: filter?.keyAccount,
    packageSizeName: filter?.size,
    productCategoryName: filter?.category,
    portfolioGroupName: filter?.mg4PortfolioGroup,
    packageName: filter?.count,
    packageTypeName: filter?.container,
    packTypeName: filter.ppg,
    customerGlobalName: filter?.global,
    customerSectorNumber: filter?.customerSector,
    customerRegionName: filter?.customerRegion,
    customerGroupName: filter?.customerGroup,
    customerBusinessUnitName: filter?.businessUnit,
    customerMarketUnitName: filter?.marketUnit,
    ibpDemandGroup: filter?.ibpDemandGroup,
    customerTradeSegmentDescription: filter?.cg1TrageSegment,
    salesSectorName: filter?.salesSector,
    salesRegionName: filter?.salesRegion,
    salesCountryCode: filter?.salesCountry,
    salesOrganizationCode: filter?.businessOrg,
    salesDivisionName: filter?.division,
    forecastEngineClusterId: filter?.clusters,
    pageNumber: 1,
    snapshotDate:
      filter?.term.toString() === "ST"
        ? filter?.shortSnapshotDate
        : filter?.midSnapshotDate,
    forecastModuleCode: filter?.ac1ac2,
    forecastEngineSegmentationId: filter?.segment,
    pageSize: PAGE_LIMIT,
    sector: userData?.currentSector?.sector,
    market: userData?.currentSector?.market,
    // LATAM Key
    flavorName: filter?.flavourNames,
    customerChannelName: filter?.channels,
    customerDemandGroup: filter?.demandGroups,
    customerSubChannelName: filter?.subChannels,
    customerRegionalCluster: filter?.regionalClusters,
    locationGlobalName: filter?.globalCode,
    locationSectorName: filter?.locationSector,
    locationCountryName: filter?.locationCountry,
    planningLocationId: filter?.planningLocationId,
    locationRegionName: filter?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
    salesZoneName: filter?.zone,
    eachUPC: filter?.eachUPC,
  };
  // commented measurement CR changes, will be required in future
  if (userData?.currentSector?.sector === "LATAM") {
    forecastAnalysisApiRequestBody["isMeasurement"] = false;

    // flag added for handling measurement requirement
    // forecastAnalysisApiRequestBody["isMeasurement"] =
    //   measurementToggleActiveComparisonPredefined === "measurementLevel"
    //     ? true
    //     : false;
  }

  const applyFilters = async () => {
    dispatch(setLoader(true));
    const chartDataResponse = await postAPI(
      "predefinedbacktesting/chart",
      {
        ...payloadForChart,
        rank: filter?.rankBackTestingPredefined.map((item) => +item),
      },
      authState?.accessToken?.accessToken!
    );

    if (
      userData?.sections?.predefinedRuns?.table?.lagDropdown &&
      filter?.term?.toString() === "ST" &&
      lagDropdownData.predefinedLagForSt !== null
    ) {
      forecastAnalysisApiRequestBody["forecastLagNumber"] = Number(
        lagDropdownData.predefinedLagForSt
      );
    }
    if (
      userData?.sections?.predefinedRuns?.table?.lagDropdown &&
      filter?.term?.toString() === "MT" &&
      lagDropdownData.predefinedLagForMt !== null
    ) {
      forecastAnalysisApiRequestBody["forecastLagNumber"] = Number(
        lagDropdownData.predefinedLagForMt
      );
    }

    const forecastAnalysisApiRequestBodyTable = {
      shortTermMidTermCode: filter?.term.toString(),
      brandName: filter?.brand,
      subBrandName: filter?.subBrand,
      rank: filter?.rankBackTestingPredefined?.map((item) => +item),
      // rank: [1, 2],
      productLineName: filter?.productLine,
      productGroupName: filter?.productGroup,
      packageSizeName: filter?.size,
      packageTypeName: filter?.container,
      packTypeName: filter.ppg,
      keyAccount: filter?.keyAccount,
      materialGlobalTradeItemNumberDescription: filter?.planningItem,
      productCategoryName: filter?.category,
      eachUPC: filter?.eachUPC,
      portfolioGroupName: filter?.mg4PortfolioGroup,
      packageName: filter?.count,
      salesRegionName: filter?.salesRegion,
      salesZoneName: filter?.zone,
      salesCountryCode: filter?.salesCountry,
      forecastModuleCode: filter?.ac1ac2,
      forecastEngineSegmentationId: filter?.segment,
      customerGlobalName: filter?.global,
      customerSectorNumber: filter?.customerSector,
      customerRegionName: filter?.customerRegion,
      customerGroupName: filter?.customerGroup,
      customerBusinessUnitName: filter?.businessUnit,
      customerMarketUnitName: filter?.marketUnit,
      ibpDemandGroup: filter?.ibpDemandGroup,
      customerTradeSegmentDescription: filter?.cg1TrageSegment,
      salesSectorName: filter?.salesSector,
      salesOrganizationCode: filter?.businessOrg,
      salesDivisionName: filter?.division,
      forecastEngineClusterId: filter?.clusters,
      pageNumber: 1,
      snapshotDate:
        filter?.term.toString() === "ST"
          ? filter?.shortSnapshotDate
          : filter?.midSnapshotDate,
      pageSize: PAGE_LIMIT,
      sector: userData?.currentSector?.sector,
      market: userData?.currentSector?.market,
      // LATAM Key
      flavorName: filter?.flavourNames,
      customerChannelName: filter?.channels,
      customerDemandGroup: filter?.demandGroups,
      customerSubChannelName: filter?.subChannels,
      customerRegionalCluster: filter?.regionalClusters,
      locationGlobalName: filter?.globalCode,
      locationSectorName: filter?.locationSector,
      locationCountryName: filter?.locationCountry,
      planningLocationId: filter?.planningLocationId,
      locationRegionName: filter?.regionCode,
      // PFNA key
      alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
    };

    resultVisualisationAverageAPI(forecastAnalysisApiRequestBody, authState)
      .then((averageData) => {
        dispatch(setLoader(true));
        dispatch(setAverageData(averageData));
        dispatch(setLoader(false));
      })
      .catch((err) => console.log("Error", err));

    const chartData = chartDataResponse?.data?.response?.chartData;
    dispatch(setChartData(chartData));
    makeBackTesingApiCall(forecastAnalysisApiRequestBodyTable, authState);
  };
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("invoke") === "true") {
      applyFilters();

      setTimeout(() => {
        navigate("/resultvisualization", { replace: true });
      }, 1000);
    }
  }, [searchParams, navigate]);

  const makeBackTesingApiCall = (
    forecastAnalysisApiRequestBodyTable: any,
    authState: any
  ) => {
    dispatch(
      resultVisulizationBackTestingTableDataSetter({
        isTableDataLoading: true,
        backTestingTableData: [],
        tableHeader: [],
      })
    );

    const groupByOptionsKeyValues =
      resultVisulizationBackTestingPagination?.groupBySelectedField !==
        undefined &&
      resultVisulizationBackTestingPagination?.groupBySelectedField?.length > 0
        ? resultVisulizationBackTestingPagination?.groupBySelectedField
        : userData?.sections?.predefinedRuns?.table?.defaultDataShown
            ?.backTesting;

    const groupByPFNAData =
      userData?.currentSector?.sector === "PFNA" &&
      filter?.ac1ac2?.[0] !== undefined &&
      filter?.ac1ac2?.[0] === "AC1"
        ? groupByOptionsKeyValues?.filter(
            (item) => item !== "Planning Item" && item !== "UPC"
          )
        : groupByOptionsKeyValues;

    const backTestingGroupByOptions = mapperForGroupByLableToKey(
      groupByPFNAData,
      dataShowKeyValueMappings
    );

    const resultVisulizationTestingApiRequestBody: any = {
      ...forecastAnalysisApiRequestBodyTable,
      pageNumber: resultVisulizationBackTestingPagination?.pageNumber,
      pageSize: resultVisulizationBackTestingPagination?.pageLimit,
      groupBy: backTestingGroupByOptions?.selectedGroupByOptions,
    };

    if (
      userData?.sections?.predefinedRuns?.table?.lagDropdown &&
      filter?.term?.toString() === "ST" &&
      lagDropdownData.predefinedLagForSt !== null
    ) {
      resultVisulizationTestingApiRequestBody["forecastLagNumber"] = Number(
        lagDropdownData.predefinedLagForSt
      );
    }
    if (
      userData?.sections?.predefinedRuns?.table?.lagDropdown &&
      filter?.term?.toString() === "MT" &&
      lagDropdownData.predefinedLagForMt !== null
    ) {
      resultVisulizationTestingApiRequestBody["forecastLagNumber"] = Number(
        lagDropdownData.predefinedLagForMt
      );
    }

    // commented measurement CR changes, will be required in future
    if (userData?.currentSector?.sector === "LATAM") {
      resultVisulizationTestingApiRequestBody["isMeasurement"] = false;

      // flag added for handling measurement requirement
      // resultVisulizationTestingApiRequestBody["isMeasurement"] =
      //   measurementToggleActivePredefinedTable === "measurementLevel"
      //     ? true
      //     : false;
    }

    setTablePayload(resultVisulizationTestingApiRequestBody);

    resultVisualisationTableAPI(
      resultVisulizationTestingApiRequestBody,
      authState
    )
      .then((response) => {
        dispatch(setLoader(true));
        const totalBackTestingPages = calculateTotalPages(
          PAGE_LIMIT,
          response?.totalCount
        );
        dispatch(
          updateBackTestingTotalPages({
            totalPages: totalBackTestingPages,
            pageLimit: resultVisulizationBackTestingPagination.pageLimit,
            pageNumber: resultVisulizationBackTestingPagination.pageNumber,
            isDataShownClicked:
              resultVisulizationBackTestingPagination.isDataShownClicked,
          })
        );
        dispatch(
          resultVisulizationBackTestingTableDataSetter({
            tableHeader: response?.dynamicTableHeader,
            backTestingTableData: response?.dfuBasedGroupedData,
            isTableDataLoading: false,
          })
        );
        dispatch(setLoader(false));
      })
      .catch((error) => {
        console.log("resultVisulizationTestingTableApi error", error);
      });
  };

  return (
    <>
      <div className="p-5 bg-secondary-bg mt-4 rounded">
        {open && (
          <FilterResetModal
            open={open}
            resetFilter={resetFilter}
            handleClose={handleClose}
          />
        )}
        <FilterButtons handleOpen={handleOpen} applyFilters={applyFilters} />
        <FilterDropdown />
      </div>
      <ComparisonResultPredefinedRuns
        data={overallAverage}
        measurementPayload={forecastAnalysisApiRequestBody}
      />
      {tablePayload !== null && (
        <ResultVisulizationBacktestingResults
          widthColumn="12%"
          isResultVisulizationPage={true}
          isExperimentationPage={false}
          measurementPayload={tablePayload}
          isPredefined={true}
        />
      )}
    </>
  );
};

export default ResultVisualizationPredefinedFilter;
